import { type EventInput } from 'fullcalendar';
import { type BackendCalendarEvent } from '@classic/Components/EventsSlider/Schemas';
import { toSiteWallClock } from './TimeFormat';

/**
 * Everything the full-page calendar needs to render tooltips, list rows, and
 * click-through navigation, carried on the FullCalendar event's extendedProps.
 *
 * The aggregating `/api/calendars/{id}/events` endpoint (shared with the
 * Calendar Events component) does NOT expose the v3-only `map`, `directions`,
 * or `attachments` feed fields, so those are intentionally absent here.
 */
export interface PageCalendarEventProps {
    description: string;
    address: string | null;
    calendarId: number;
    calendarName: string;
    feedSource: string | null;
    link: string | null;
    eventUrl: string;
    startDatetime: string;
    endDatetime: string;
    startTime: string | null;
    endTime: string | null;
    startDate: string;
    endDate: string;
    allDay: boolean;
    siteTimezone: string;
}

/**
 * Map a backend event to a FullCalendar v7 `EventInput`.
 *
 * - v7 field renames: backgroundColor/borderColor -> `color`, textColor -> `contrastColor`.
 * - Timed events are pinned to the site wall-clock so they land in the same
 *   timeGrid slot for every viewer. All-day events keep their raw datetimes
 *   (matching the shipped, tested Calendar Events component behavior).
 * - `url` is deliberately NOT set; click-through is handled by an explicit
 *   `eventClick` handler that branches on feed source, reproducing v3.
 */
export const transformBackendEvent = (
    event: BackendCalendarEvent,
    siteTimezone: string,
): EventInput => {
    const start = event.all_day
        ? event.start_datetime
        : toSiteWallClock(event.start_datetime, siteTimezone);
    const end = event.end_datetime
        ? (event.all_day ? event.end_datetime : toSiteWallClock(event.end_datetime, siteTimezone))
        : undefined;

    const extendedProps: PageCalendarEventProps = {
        description: event.description,
        address: event.address ?? null,
        calendarId: event.calendar_id,
        calendarName: event.calendar_name,
        feedSource: event.feed_source ?? null,
        link: event.link ?? null,
        eventUrl: event.event_url,
        startDatetime: event.start_datetime,
        endDatetime: event.end_datetime,
        startTime: event.start_time,
        endTime: event.end_time,
        startDate: event.start_date,
        endDate: event.end_date,
        allDay: event.all_day,
        siteTimezone,
    };

    return {
        id: event.id,
        title: event.title,
        start,
        end,
        allDay: event.all_day,
        color: event.color,
        contrastColor: event.text_color,
        extendedProps,
    };
};
